import {
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { ArrowBack, NotificationsActive } from "@material-ui/icons";
import { useSB } from "../../../contexts/SupabaseContext";
import { useAuth } from "../../../contexts/AuthContext";
import SnapshotTiles from "../../user-profile/SnapshotTiles";
import SendAlertModal from "../../manage-dealers/dealer-detail-r/SendAlertModal";

// import ChangeSubDealerLevel from "./ChangeSubDealerLevel";
// import ChangeSubDealerParent from "./ChangeSubDealerParent";

/**
 * Component for displaying details of a sub dealer.
 * It fetches sub dealer and parent dealer information, displays them, and allows
 * for changing the level and parent of the sub dealer.
 *
 * State:
 * - dealer: Contains sub dealer details.
 * - parent: Contains parent dealer details.
 * - dealerLoaded: Boolean controlling when the component should load the sub dealer details.
 * - changeLevelOpen: Boolean controlling the change level dialog.
 * - changeParentOpen: Boolean controlling the change parent dialog.
 *
 * Effects:
 * - Fetches sub dealer and parent dealer details on component mount and when `id` changes.
 */
export default function SubDealerDetail() {
  const { dealerId } = useParams();
  const history = useHistory();
  const { GetTableWhere, updateRow } = useSB();
  const [d, setD] = useState(null);
  const [isLeader, setIsLeader] = useState(false);
  const { currentUser } = useAuth();
  const [alertModalOpen, setAlertModalOpen] = useState(false);

  /**
   * Gets the sub dealer details given a sub dealer ID.
   * @param {string} subId - The ID of the sub dealer.
   * @returns {Promise<{dealer: object, parent: object}>} - A promise resolving to an object
   * with keys "dealer" and "parent", each pointing to the respective
   * dealer and parent dealer objects.
   * @throws {Error} - If the dealer or parent dealer does not exist.
   */
  const getSubDealerDetails = async subId => {
    // Get the dealer document by its ID
    const dealerData = await GetTableWhere("users_master", ["id", "eq", subId]);

    if (!dealerData) {
      throw new Error(`Dealer with ID ${subId} not found`);
    }

    setD({
      dealer: dealerData[0],
      // parent: parentData[0],
    });
    setIsLeader(dealerData[0].is_leader);
  };

  useEffect(() => {
    getSubDealerDetails(dealerId);
  }, [dealerId]);

  const handleIsLeader = event => {
    setIsLeader(event.target.checked);

    updateRow("users_master", dealerId, {
      is_leader: event.target.checked,
      is_leader_date: event.target.checked ? new Date() : null,
      last_updated_by_auth_id: currentUser.auth_id,
    });
  };

  return (
    <div>
      {" "}
      <h1>{`Z-${d?.dealer.id}: ${d?.dealer.first_name} ${d?.dealer.surname}`}</h1>
      <div className="back-link">
        <Button
          color="primary"
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => history.goBack()}
        >
          Back
        </Button>{" "}
        {d !== null ? (
          <SnapshotTiles currentUser={d.dealer} otherProfile />
        ) : null}
      </div>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={6}>
          <Card>
            <CardContent>
              <Typography>Phone Number: {d?.dealer.phone}</Typography>
              <Typography>Email: {d?.dealer.email}</Typography>
              <Typography>Street: {d?.dealer.street}</Typography>
              <Typography>City: {d?.dealer.city}</Typography>
              <Typography>Province: {d?.dealer.province}</Typography>
            </CardContent>
            <Button
              startIcon={<NotificationsActive />}
              variant="contained"
              color="primary"
              style={{ marginLeft: 14 }}
              onClick={() => setAlertModalOpen(true)}
            >
              Send alert
            </Button>
            <CardContent>
              <div>
                <FormControlLabel
                  control={
                    <Switch
                      disabled={isLeader || d?.dealer.is_leader_blocked}
                      checked={isLeader}
                      onChange={handleIsLeader}
                      name="customSwitch"
                      color="primary"
                    />
                  }
                  label="Trainer Status"
                />
                <Typography variant="body1" style={{ marginTop: "16px" }}>
                  Dealer is{" "}
                  {isLeader && d?.dealer.is_leader
                    ? `Trainer since ${d?.dealer.is_leader_date}`
                    : "not a Leader"}
                </Typography>
                {isLeader && (
                  <Typography variant="body1" style={{ marginTop: "16px" }}>
                    <i>
                      You cannot change the Trainer status if user is already a
                      Leader or if the toggle has been disabled by an admin.
                    </i>
                  </Typography>
                )}
                {d?.dealer.is_leader_blocked && (
                  <Typography variant="body1" style={{ marginTop: "16px" }}>
                    <i>Toggle has been disabled by an admin.</i>
                  </Typography>
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>{" "}
      <SendAlertModal
        open={alertModalOpen}
        onClose={() => setAlertModalOpen(false)}
        dealer={d?.dealer}
      />
    </div>
  );
}
